import React, { useState, useEffect, useRef } from 'react';
import { MdLocationOn } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import ENDPOINTS from '../../api/endpoints';

export interface PlaceResult {
  placeId: string;
  description: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

interface AddressAutocompleteInputProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectPlace?: (place: PlaceResult) => void;
  error?: string;
  placeholder?: string;
  required?: boolean;
}

export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onSelectPlace,
  error,
  placeholder = 'Start typing address...',
  required = false,
}) => {
  const [predictions, setPredictions] = useState<PlaceResult[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Flag to track if the current value change came from selecting a suggestion
  const isSelectingRef = useRef<boolean>(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced Google Places Search Effect using ENDPOINTS with duplication check guard
  useEffect(() => {
    const query = value || '';

    // If the change was triggered by selecting a suggestion, ignore this effect run and reset flag
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    
    if (query.trim().length < 2) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsSearching(true);
        
        const apiPath = ENDPOINTS.GOOGLE_PLACES_SEARCH_API_PATH || '/snapmenu/v1/google/search';
        const baseUrl = ENDPOINTS.API_V2_BASE_URL || 'https://dev-hub.snapmenu.ai';
        const requestUrl = `${baseUrl}${apiPath}?query=${encodeURIComponent(query)}`;

        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch places: ${response.statusText}`);
        }

        const jsonResponse = await response.json();
        
        const results = jsonResponse.data || jsonResponse.predictions || jsonResponse || [];
        setPredictions(results);
        setShowDropdown(results.length > 0);
      } catch (err) {
        console.error('Error fetching place predictions:', err);
        setPredictions([]);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [value]);

  const handleSelectPrediction = (place: PlaceResult) => {
    // Mark that we are programmatically setting the value via selection
    isSelectingRef.current = true;
    
    setShowDropdown(false);
    setPredictions([]);

    // Use the specific 'name' or fallback to the first part of the address
    const streetAddress = place.name || (place.address ? place.address.split(',')[0] : place.description);
    
    const syntheticEvent = {
      target: {
        name,
        value: streetAddress,
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    onChange(syntheticEvent);

    if (onSelectPlace) {
      onSelectPlace(place);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          id={id}
          name={name}
          value={value}
          onChange={onChange}
          autoComplete="off"
          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-[#FA2C37] focus:border-[#FA2C37] focus:outline-none bg-white text-gray-900 ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={placeholder}
          aria-describedby={error ? `${id}-error` : undefined}
          aria-invalid={!!error}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          {isSearching ? (
            <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <MdLocationOn className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      <AnimatePresence>
        {showDropdown && predictions.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            {predictions.map((place, index) => (
              <motion.li
                key={place.placeId || index}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => handleSelectPrediction(place)}
                className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-start space-x-2 text-sm"
              >
                <MdLocationOn className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-medium text-gray-900">
                    {place.name || place.description}
                  </span>
                  {place.address && (
                    <p className="text-xs text-gray-500">
                      {place.address}
                    </p>
                  )}
                </div>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>

      {error && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};